import _defineProperty from "/workdir/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";
import _asyncToGenerator from "/workdir/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/asyncToGenerator.js";
import _objectWithoutProperties from "/workdir/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/objectWithoutProperties.js";
var _excluded = ["appearance", "className", "customFormIdentifier", "customTrackingType", "overrideConsent", "onSubmit", "onSuccess"];
import _regeneratorRuntime from "/workdir/node_modules/next/dist/compiled/@babel/runtime/regenerator/index.js";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useState, useEffect } from 'react';
import { v1 as uuidv1 } from 'uuid';
import { useForm, FormProvider } from 'react-hook-form';
import * as z from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAppearance, AppearanceProvider } from '@segment/matcha/foundations/AppearanceContext';
import { CustomFormIdentifierField } from '@segment/templates/FormFields';
import Disclaimer from '@segment/templates/Disclaimer';
import FormSubmitAction from '@segment/templates/FormSubmitAction';
import { CompanyJobTitleField, companyJobTitleMessages } from '@segment/templates/FormFields/CompanyJobTitle';
import { FullNameField, fullNameMessages } from '@segment/templates/FormFields/FullName';
import { CompanyEmailField, companyEmailMessages } from '@segment/templates/FormFields/CompanyEmail';
import { identify, trackProgramSignupForm, trackServerError } from '@segment/tracking/scripts/helpers';
import formRegex from '@segment/utils/regexes/form';
import sharedEmailRegex from '@segment/utils/regexes/sharedEmail';
import emailRegex from '@segment/utils/regexes/email';
import * as views from './GetAccessForm.views';
import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { jsxs as _jsxs } from "@emotion/react/jsx-runtime";
var BUTTON_TEXT = "Get Access";
export var errorMessage = {
  heading: "Something happened",
  description: "We are really sorry. There was an error processing your submission. Try reloading your browser, and submitting again."
};
/* eslint-disable camelcase */

var schema = z.object({
  companyJobTitle: z.string({
    required_error: companyJobTitleMessages.required
  }).regex(formRegex, {
    message: companyJobTitleMessages.invalid
  }),
  name: z.string({
    required_error: fullNameMessages.required
  }).regex(formRegex, {
    message: fullNameMessages.invalid
  }),
  email: z.string({
    required_error: companyEmailMessages.required
  }).regex(emailRegex, {
    message: companyEmailMessages.invalid
  }).regex(sharedEmailRegex, {
    message: companyEmailMessages.sharedEmail
  })
}).partial({
  name: true,
  companyJobTitle: true
});
/* eslint-enable camelcase */
// Set up demo form props since we define some of these props here in the template

export var GetAccessForm = function GetAccessForm(_ref) {
  var _methods$formState$er3, _methods$formState$er4, _methods$formState$er5, _methods$formState$er6;

  var appearance = _ref.appearance,
      className = _ref.className,
      customFormIdentifier = _ref.customFormIdentifier,
      customTrackingType = _ref.customTrackingType,
      overrideConsent = _ref.overrideConsent,
      onSubmit = _ref.onSubmit,
      onSuccess = _ref.onSuccess,
      rest = _objectWithoutProperties(_ref, _excluded);

  var formAppearance = useAppearance(appearance); // Initialize form methods.

  var methods = useForm({
    mode: "onBlur",
    reValidateMode: "onBlur",
    shouldUnregister: true,
    resolver: zodResolver(schema)
  });

  var _useState = useState(false),
      needsAccess = _useState[0],
      setNeedsAccess = _useState[1];

  var _useState2 = useState(false),
      isLoading = _useState2[0],
      setLoading = _useState2[1];

  var _useState3 = useState(""),
      prevEmail = _useState3[0],
      setPrevEmail = _useState3[1];

  var watchEmail = methods.watch("email");
  useEffect(function () {
    if (methods && needsAccess) {
      if (watchEmail === prevEmail) {
        methods.setFocus("name");
      } else {
        methods.setFocus("email");
        setNeedsAccess(false);
      }
    }
  }, [methods, needsAccess, prevEmail, watchEmail]);

  var submitCallback = /*#__PURE__*/function () {
    var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(data) {
      var _methods$formState$er, _methods$formState$er2;

      var uniqueId, result;
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              uniqueId = uuidv1();
              _context.prev = 1;
              _context.next = 4;
              return identify(_objectSpread(_objectSpread(_objectSpread(_objectSpread({}, data), customFormIdentifier && {
                customFormIdentifier: customFormIdentifier
              }), customTrackingType && {
                customTrackingType: customTrackingType
              }), {}, {
                server_message_id: uniqueId // eslint-disable-line camelcase

              }), overrideConsent);

            case 4:
              _context.next = 10;
              break;

            case 6:
              _context.prev = 6;
              _context.t0 = _context["catch"](1);
              trackServerError(data, customFormIdentifier || "", JSON.stringify(_context.t0));
              methods.setError("root.serverError", {
                type: "400"
              });

            case 10:
              if (!(!((_methods$formState$er = methods.formState.errors) !== null && _methods$formState$er !== void 0 && (_methods$formState$er2 = _methods$formState$er.root) !== null && _methods$formState$er2 !== void 0 && _methods$formState$er2.serverError) && onSubmit)) {
                _context.next = 16;
                break;
              }

              setLoading(true);
              _context.next = 14;
              return onSubmit(data);

            case 14:
              result = _context.sent;

              if (result) {
                methods.clearErrors();
                if (data.name && data.companyJobTitle) trackProgramSignupForm(_objectSpread(_objectSpread({}, data), {}, {
                  customFormIdentifier: customFormIdentifier
                }));
                if (onSuccess) onSuccess(data);
                setNeedsAccess(false);
              } else {
                methods.setError("form", {
                  type: "server",
                  message: "user email not found"
                });
                setPrevEmail(methods.getValues("email"));
                setTimeout(function () {
                  setLoading(false);
                  setNeedsAccess(true);
                }, 50);
              }

            case 16:
            case "end":
              return _context.stop();
          }
        }
      }, _callee, null, [[1, 6]]);
    }));

    return function submitCallback(_x) {
      return _ref2.apply(this, arguments);
    };
  }();

  return _jsx(AppearanceProvider, {
    appearance: formAppearance,
    children: _jsx(views.Wrapper, {
      id: "get-access-form",
      children: _jsx(FormProvider, _objectSpread(_objectSpread({}, methods), {}, {
        children: _jsxs(views.FormWrapper, _objectSpread(_objectSpread({
          className: className,
          noValidate: true,
          onSubmit: methods.handleSubmit(submitCallback),
          "data-hook": "get-access-form"
        }, rest), {}, {
          children: [!methods.formState.isSubmitSuccessful && !((_methods$formState$er3 = methods.formState.errors) !== null && _methods$formState$er3 !== void 0 && (_methods$formState$er4 = _methods$formState$er3.root) !== null && _methods$formState$er4 !== void 0 && _methods$formState$er4.serverError) && _jsxs(views.FormContent, {
            children: [_jsx(CompanyEmailField, {}), needsAccess && _jsx(FullNameField, {}), needsAccess && _jsx(CompanyJobTitleField, {}), customFormIdentifier && _jsx(CustomFormIdentifierField, {
              value: customFormIdentifier
            }), _jsx(views.StyledButton, {
              color: "primary",
              variant: "filled",
              size: "large",
              expanded: true,
              isLoading: isLoading,
              children: BUTTON_TEXT
            }), _jsx(Disclaimer, {})]
          }), (_methods$formState$er5 = methods.formState.errors) !== null && _methods$formState$er5 !== void 0 && (_methods$formState$er6 = _methods$formState$er5.root) !== null && _methods$formState$er6 !== void 0 && _methods$formState$er6.serverError ? _jsx("div", {
            role: "alert",
            children: _jsx(FormSubmitAction, _objectSpread({
              status: "error"
            }, errorMessage))
          }) : null]
        }))
      }))
    })
  });
};
export default GetAccessForm;